/** 결제 이력과 정산 명세. 목업 `A-43`·`A-43b` 와 `W-70` 이 같은 값을 쓴다.
 *
 * **화면에 나가는 낱말은 PRD 7.5.9 용어 표를 따른다** — `정산` · `돌아간 금액` ·
 * `정산 명세` · 칩 `일부 돌아감`·`전부 돌아감`. `환불`은 이 파일의 **타입 이름과 주석**
 * 에만 남는다(`RefundStatementView` · `refundedAmount`). 이름은 DB 컬럼과 짝이라 바꾸지
 * 않고, 문자열만 표의 낱말로 돌려준다.
 *
 * **작업 한 건마다 결제하므로 결제 줄 수가 곧 작업 수다**(`A-43`). 크레딧 충전도 월 구독도
 * 아니라서 이 목록은 작업 목록과 1:1 로 붙는다.
 *
 * **금액의 정본은 언제나 청구 항목 합계다.** `Payment.refundedAmount` 같은 캐시 컬럼과
 * 어긋나면 항목 쪽이 이긴다. 이 파일의 어느 필드도 화면에서 다시 곱하지 않는다.
 * 목업이 합계를 `₩192` 로 썼다가 `7장 × ₩27 = ₩189` 로 정정된 이력이 그 이유다(`cost.ts`).
 *
 * ⚠️ **이 주석과 아래 주석들의 `₩27` 은 2026-09-13 이전의 값이다.** 그때는 청구가가 미정이라
 * 화면이 원가를 그대로 쓰고 있었다. 지금 셀러가 내는 단가는 `CUTOUT_UNIT_PRICE_KRW`(₩500)
 * 이고, 원가 ₩27 은 `CUTOUT_UNIT_COST_KRW` 로 남아 마진 계산에만 쓴다. **옛 숫자를 지우지
 * 않는 이유**는 그 정정 사건이 "곱셈을 한 곳에 둔다"는 규칙의 근거이기 때문이다. 여기서
 * 중요한 것은 숫자가 아니라 **금액 = 단가 × 청구 장수** 라는 관계이고, 그 관계는 그대로다.
 *
 * ## 이 파일이 말하는 금액은 **저장된 행에서만** 온다 (2026-09-13 확정. `DATA_DICTIONARY` 15장)
 *
 * 결제 이력을 표본에서 실제 작업으로 갈아끼우는 회차에 정본을 못박았다.
 *
 * | 묻는 것 | 답 |
 * |---|---|
 * | 이력 한 줄의 정본 | **`Payment` 한 행 + 그에 매달린 `BillingItem` 행들** |
 * | `JobEvent.costAmount` 로 만들어도 되나 | **⛔ 안 된다.** 그 칸은 `0002` 가 "표시용 사본. 청구·정산 판정에 쓰지 않는다"고 못박았고, **값도 틀린다.** 공급자를 **부르기 전에** 적히므로 실패한 호출에도 ₩500 이 남는다(15.2) |
 * | 호출 횟수 × 단가로 만들어도 되나 | **⛔ 안 된다.** `JobImage.providerCallCount` 는 **성공·실패를 함께 센다.** 다시 만들다 실패한 호출이 있으면 실제 청구보다 크게 나온다(`A-12` 이력 합계표가 지금 그 함정에 배너를 달아 두고 있다) |
 *
 * **그래서 이 파일의 어느 타입에도 `단가`·`호출 횟수` 칸을 두지 않는다.** 칸이 있으면
 * 누군가 곱한다. 화면이 곱할 재료를 안 주는 것이 이 규칙을 지키는 가장 싼 방법이다.
 *
 * **불변식 하나로 적으면: 청구는 성공한 호출에만 붙는다.** 저장 쪽에서는
 * `BillingItem.billable = 1` 이 그 판정이고, 응답 쪽에서는 `billedQuantity` 가 그 수다.
 */

import type { PaymentStatus, RefundReason, SettlementMode } from "./domain";
import { formatDateTime } from "./format";
import { paymentStatusLabel, type StatusLabel } from "./job-labels";
import type { DateRangeContext } from "./search";

/** 결제 이력 한 줄(`A-43`·`W-70`).
 *
 * 보조줄(`09-06 · 6장 중 3장`)은 문구를 서버가 만들지 않는다. 숫자를 내려주고
 * 화면이 `paymentHistorySubline()` 하나로 쓴다. 앱과 웹이 각자 조립하면 같은 결제가 두
 * 문장으로 보인다. */
export interface PaymentHistoryItemView {
  paymentId: string;
  jobId: string;
  /** 어느 작업인지. 상품명이 없으면 목록이 `₩189` 만 남아 아무것도 못 가리킨다. */
  productName: string | null;
  status: PaymentStatus;
  /** 셀러가 부담하는 금액.
   *
   * **식이 하나로 박혀 있다**: `Σ BillingItem.amount − Σ RefundItem.amount`. 앞의 합은
   * **`billable` 로 거르지 않는다.**
   *
   * ⚠️ **거르면 정산 방식 하나에서 두 번 빠진다.** O-13 이 아직 열려 있어 두 방식이 다
   * 살아 있는데(`SettlementMode`), `CAPTURE_THEN_REFUND` 는 실패한 장도 일단 청구하고
   * 나중에 돌려준다. 그 행은 `billable = 0` 인데 `amount > 0` 이고 환불 줄이 붙는다.
   * 앞의 합에서 `billable = 1` 만 세면 그 금액은 애초에 안 들어갔는데 뒤에서 또 빠져서
   * **셀러가 낸 것보다 적게 나온다.** 다른 방식(`AUTH_THEN_PARTIAL_CAPTURE`)은 실패 행의
   * `amount` 가 0 이라 거르든 말든 결과가 같다. **그래서 안 거르는 쪽이 두 방식 모두에서
   * 옳은 유일한 식이다.**
   *
   * ✅ **`apps/api/src/lib/jobs.ts` 의 `billedAmount`·`getJobBillingSummary` 가 이 식으로
   * 맞춰졌다 (2026-09-14).** 그 두 자리가 `billable = 1` 로 거른 합을 내고 있었고, **행이
   * 0 이라 아직 틀린 값을 내지 않았을 뿐**이었다. 청구 항목 쓰기가 서는 회차에 함께 고쳤다
   * (`docs/DATA_DICTIONARY.md` 15.7 · 16장). */
  netAmount: number;
  /** **올린 장수.** 보조줄 `8장 중 7장` 의 앞 숫자가 이 값이다(PRD 7.6.21).
   *
   * `JobImage` 행 수이고 청구·환불과 독립이다. **`billedQuantity + refundedQuantity` 로
   * 만들면 안 된다**. 두 값이 갈리는 경로가 실제로 있다(2026-09-08 이중 코드리뷰).
   * 환불 경계가 "provider 를 호출했는가"라, `providerCallCount = 0` 으로 끝난 장
   * (용량 초과·provider 미설정)은 **청구도 환불도 없다.** 8장 올림 · 7장 성공 · 1장 거절이면
   * 합으로 만든 앞 숫자가 `7` 이 되어 8장을 올린 셀러가 한 장이 어디로 갔는지 알 수 없다.
   *
   * 앱은 같은 값을 `totalImages` 라 부른다(`my/payments.tsx`). 이름이 다른 것은 이 필드가
   * `apps/api/src/lib/jobs.ts` 의 SQL 별칭과 짝이기 때문이다(6.3 과 같은 규칙).
   *
   * ⚠️ 서버가 아직 이 값을 채우지 않는다. **스키마 변경은 아니다**. `JobImage` 를
   * `COUNT(*)` 하는 집계 별칭 하나가 필요할 뿐이다(`docs/DATA_DICTIONARY.md` 6.5). */
  uploadedQuantity: number;
  /** 청구된 장수. **곧 만든 장수이고, 보조줄 `8장 중 7장` 의 뒤 숫자가 이 값이다**
   *  (PRD 7.6.21, 확정 2026-09-08). 앱은 같은 값을 `chargedImages` 라 부른다
   *  (`my/payments.tsx`). 이름이 다른 것은 이 필드가 DB 컬럼과 짝이기 때문이다.
   *
   * **`COUNT(BillingItem WHERE billable = 1)` 이다.** 곧 **성공한 공급자 호출의 수**이고,
   * `JobImage` 행 수도 `providerCallCount` 합도 아니다. 셋이 다 갈린다. 부르지 않고 끝난
   * 장은 행이 없고, 실패한 호출은 행이 있어도 `billable = 0` 이다. */
  billedQuantity: number;
  /** 돌아간 장수. **보조줄의 어느 숫자도 이 값이 아니다.**
   *
   * 뒤 숫자로 쓰면 같은 행의 금액과 어긋난다(금액이 `단가 × 3` 이라 그 `3` 은 청구 장수다).
   * 앞 숫자로도 쓰지 않는다. 위 `uploadedQuantity` 설명이 그 이유다. 이 값이 답하는 질문은
   * 하나뿐이다: **돌아간 게 있는가**(칩 `일부 돌아감`·`전부 돌아감`). */
  refundedQuantity: number;
  refundedAmount: number;
  /** 왜 돌아갔나. `null` = 돌아간 것이 없다.
   *
   * **보조줄이 이 값을 봐야 한다.** `PaymentStatus.REFUNDED` 만으로는 "만들려 시도했는데 다
   * 실패한 것"과 "시작 전에 취소해 전액이 돌아간 것"을 가를 수 없다. 둘 다 전액 환불이라
   * 상태가 같다. `Refund.reason` 컬럼이 이미 있으니 집계에 별칭 하나를 더하면 된다
   * (스키마 변경 아님). 한 결제에 환불이 여러 건이면 **마지막 건의 사유**다. */
  refundReason: RefundReason | null;
  /** 결제 시각. 매입 전이면 `null` 이라 목록은 `createdAt` 순으로 그린다. */
  paidAt: string | null;
  createdAt: string;
}

/** 이 줄이 **어느 달에 서는가**. 목록 정렬 기준도 같은 값이다.
 *
 * **`paidAt` 이 null 일 수 있어서 고르는 자리를 하나로 둔다.** 결제사가 아직 없어 지금
 * 만들어지는 행은 전부 `PENDING` 이고 `paidAt` 이 비어 있다(`DATA_DICTIONARY` 15.3).
 * 앱과 웹이 각자 `paidAt ?? createdAt` 을 적으면 한쪽이 빠뜨리는 날 **월 머리글과 줄의
 * 날짜가 다른 달을 가리킨다.**
 *
 * **필드가 아니라 함수인 이유**: 서버가 이미 월로 묶어 내려주므로 클라이언트는 이 값을
 * 묶는 데 쓰지 않는다. 줄에 날짜를 찍을 때만 쓴다. 응답에 칸을 하나 더 두면 같은 사실이
 * 세 칸(`paidAt`·`createdAt`·그 칸)에 적히고, 그중 둘이 어긋나는 날이 온다. */
export function paymentHistoryOccurredAt(item: PaymentHistoryItemView): string {
  return item.paidAt ?? item.createdAt;
}

/** 월 머리글(`2026년 9월  ₩270`). **스크롤에 붙어 따라오고 합계가 그 안에 들어간다**.
 *  월 끝의 합계 줄을 따로 두면 같은 숫자가 한 화면에 두 번 나온다(`A-43`).
 *
 * **묶는 일을 서버가 한다.** 이유가 둘이다.
 *  ① **합계와 목록이 어긋나지 않는다.** 클라이언트가 묶으면 쪽 나누기가 붙는 날 반쪽만
 *     받은 달의 머리글이 그 달 전체인 척한다.
 *  ② **달 경계가 시간대를 탄다.** Workers 는 UTC 라 `2026-10-01 00:30 KST` 를 그대로
 *     묶으면 9월로 간다. 그래서 `PaymentHistoryQuery.dateRange` 로 셀러 오프셋을 받는다.
 *     `search.ts` 가 기간 칩에서 이미 푼 문제와 같은 것이고, 두 번째 해법을 만들지 않는다. */
export interface PaymentMonthGroupView {
  /** `2026-09`. 화면 문구는 클라이언트가 만든다. */
  yearMonth: string;
  /** **이 묶음에 든 줄들의 `netAmount` 합.** 그 이상도 이하도 아니다. 조건에 걸러진 줄은
   *  이미 `items` 에 없으므로 합계도 자동으로 따라간다(`A-43` 이 요구하는 동작).
   *
   * ⚠️ **"실제로 낸 금액"이라고 읽지 마라.** 결제사가 붙기 전에는 `PENDING` 줄뿐이라 이
   * 합은 **낼 금액**이다. **데이터가 그 사실을 말하는 자리는
   * `PaymentHistoryItemView.status` 와 `paidAt` 이다.**
   *
   * **낱말은 닫혔다 (2026-09-13, `design-manager`).** 머리글은 `9월 합계` 가 아니라
   * **`9월 청구`** 이고(PRD 7.5.9 · 7.6.21), 줄마다의 칩은 **`결제 전`** 이다
   * (`job-labels.ts`. 그 함수가 `결제 대기` 를 돌려주던 것은 2026-09-14 에 고쳤다).
   * **`청구` 는 두 시대 모두에서 참이다.** 우리가 얼마를 청구하는가이지 그 돈이
   * 움직였는지가 아니다. **화면이 이 합에 다른 낱말을 붙이지 않는다.** */
  totalAmount: number;
  items: PaymentHistoryItemView[];
}

export interface PaymentHistoryView {
  /** **최신 달이 먼저**이고, 달 안에서도 최신 줄이 먼저다. 정렬 기준은
   *  `paymentHistoryOccurredAt` 과 같은 값이다. */
  groups: PaymentMonthGroupView[];
  /** 조건에 걸린 줄 수. 목록이 비었을 때 "없다"와 "조건이 좁다"를 가르는 데 쓴다.
   *
   * **`groups` 안의 줄 수와 다를 수 있다.** 서버가 `PAYMENT_HISTORY_MAX_ROWS` 에서 자르기
   * 때문이고, 두 수가 다르면 잘린 것이다. 그래서 이 값은 잘라 낸 뒤에 세지 않는다. */
  matchedCount: number;
  /** 이 셀러의 전체 줄 수(조건 없이). `0` 이면 **결제가 하나도 없는 것**이고,
   *  `totalCount > 0` 인데 `matchedCount === 0` 이면 조건이 좁은 것이다. 화면이 두 경우에
   *  다른 말을 해야 해서 두 수를 함께 내린다(`jobs` 목록과 같은 규약).
   *
   * ⚠️ **이 수가 답하지 못하는 질문이 하나 있다.** "줄이 없다"는 말하지만 **"작업도
   * 없다"는 말하지 못한다.** 아래 `hasAnyJob` 이 그 자리다. */
  totalCount: number;
  /** **이 셀러가 작업을 한 건이라도 만든 적이 있는가.** 청구 줄이 아니라 **작업**을 본다.
   *
   * `A-43`·`W-70` 빈 상태의 **둘째 줄** 하나를 위해 있다(PRD 7.6.21 · 7.12 요구 6):
   * **"장당 청구가 정해지기 전에 만든 작업은 여기 줄로 서지 않습니다."** 그 문장은
   * **작업이 있는 셀러에게만 참이다.** 첫날 가입한 셀러에게 그리면 **있지도 않은 과거를
   * 말한다.** `totalCount` 로는 갈 수 없다. 그 수는 청구 줄만 세고, **지금 이 셀러가
   * 정확히 작업 2건 · 청구 줄 0줄이다**(`docs/DATA_DICTIONARY.md` 15.4 · 16장).
   *
   * **참/거짓 하나다. 작업 수를 내리지 않는다 (판정).** 수를 내리면 화면이 그것을 그리고,
   * `totalCount` 와 나란히 놓이는 순간 셀러가 둘을 빼서 **"청구 안 된 작업 n건"** 으로
   * 읽는다. 그 뺄셈은 **소급 청구가 정해지기 전에는 뜻이 없는 값**이고(`7.13` D-71),
   * 화면이 먼저 답을 말하게 된다. 그리고 저 문장에는 **애초에 숫자가 없다**(PRD 7.6.21:
   * "숫자를 적지 않는다"). 필요한 것이 참/거짓뿐이면 참/거짓만 내린다.
   *
   * **세는 대상**: 이 셀러의 `DetailJob` 행이 하나라도 있는가(`SELECT EXISTS`). **상태로
   * 거르지 않는다.** 원본을 지운 작업(`DISCARDED`)도 목록에서 사라지지 않으므로(PRD 5. ①)
   * 셀러는 그 줄을 자기 작업으로 보고 있고, 그 사람에게도 "왜 청구 내역엔 없는가"가
   * 남는다.
   *
   * ⚠️ **옵셔널인 것이 규격이다.** PRD 7.6.21 이 **"판정값이 없으면 그 줄을 그리지
   * 않는다"** 로 닫았다. 모르는 것을 아는 척하는 것보다 낫다. 그래서 `undefined` 는
   * "서버가 아직 말하지 않는다"이고 화면은 그때 둘째 줄을 그리지 않는다.
   * **`?? true` 로 채우지 마라.** 서버가 채우기 시작하면 필수로 바꾼다
   * (`JobBlockView.channel` 과 같은 경로). */
  hasAnyJob?: boolean;
}

/** 한 번에 내려주는 줄 수의 상한. **쪽 나누기 대신 이것과 연 필터를 쓴다**(아래 설명).
 *
 * 작업 한 건 = 결제 한 건이라 이 수는 곧 "한 해에 만든 상세 수"다. 넘으면
 * `matchedCount` 가 그 사실을 말하고, 그때 연 필터가 범위를 좁힌다. */
export const PAYMENT_HISTORY_MAX_ROWS = 500;

/** 결제 이력 질의. **`limit`·`offset` 이 없는 것이 결정이다.**
 *
 * ⛔ **쪽 나누기를 붙이지 마라.** 이 화면의 머리글은 월 **합계**를 들고 있고, 한 달이 두
 * 쪽에 걸치면 첫 쪽의 머리글이 그 달 전체인 척한다. **틀린 합계는 빈 목록보다 나쁘다.**
 * 셀러가 알아차릴 방법이 없다. 범위를 줄이는 수단은 이미 화면에 있다(`A-43` 의 `2026년 ⌄`).
 *
 * 정말로 필요해지면 **쪽이 아니라 달 단위로 더 받는 방식**이어야 한다(이어받는 단위가
 * 머리글의 단위와 같아야 한다). 그때 이 주석을 근거로 다시 판단한다. */
export interface PaymentHistoryQuery {
  filter?: PaymentHistoryFilter;
  /** 달 경계를 만들 기준 시각·시간대. 앱과 웹은 비워 둔다(기기 시간대가 기본값이다).
   *  서버에서 부를 때는 반드시 넘긴다. Workers 는 UTC 라 기본값이 0 이다. */
  dateRange?: DateRangeContext;
}

/** 결제 이력 질의 이름. **`JOB_SEARCH_PARAMS` 와 같은 이유로 여기 둔다.** 이름을
 *  서버와 클라이언트가 각자 적으면 한쪽 오타가 "조건이 무시된 전체 목록"으로 나간다. */
export const PAYMENT_HISTORY_PARAMS = {
  /** `PaymentHistoryScope` 값 그대로. 없으면 `ALL`. */
  scope: "scope",
  /** `2026`. 없으면 전체. */
  year: "year",
  /** 셀러 시간대의 UTC 기준 오프셋(분). KST = `540`. 달 경계가 이 값을 탄다. */
  offset: "tzOffset",
} as const;

/** `A-43` 의 상태 칩. **둘만 둔다.** 셀러가 이 화면에서 하는 일은 "이번 달 얼마 썼나"와
 * "돌아간 게 있는 건만 보기" 둘이고, `완료`만 골라 보는 수요는 전체와 거의 같다.
 * 부분·전액 구분은 목록의 상태 칩이 이미 말한다. */
export type PaymentHistoryScope = "ALL" | "REFUNDED";

export interface PaymentHistoryScopeOption {
  key: PaymentHistoryScope;
  /** 화면 문구. 값 이름(`REFUNDED`)과 다른 낱말이다 — 값은 컬럼의 말이고 라벨은 셀러의
   *  말이다(PRD 7.5.9). */
  label: string;
}

/** `A-43`·`W-70` 의 필터 칩 문구. **라벨을 화면에 두지 않는다** — `search.ts` 의
 * `JOB_STATUS_FILTERS` 와 같은 이유다. 앱이 `돌아간 것`, 웹이 `환불된 것` 이라고 쓰는 일이
 * 생기지 않게 값과 낱말을 한자리에 묶는다. 정본 목업이 쓰는 낱말 그대로다. */
export const PAYMENT_HISTORY_SCOPES: readonly PaymentHistoryScopeOption[] = [
  { key: "ALL", label: "전체" },
  { key: "REFUNDED", label: "돌아간 것" },
] as const;

export interface PaymentHistoryFilter {
  scope: PaymentHistoryScope;
  /** `2026`. 연 단위로 좁힌다(`A-43` 의 `2026년 ⌄`). `null` = 전체. */
  year: number | null;
}

export const DEFAULT_PAYMENT_HISTORY_FILTER: PaymentHistoryFilter = {
  scope: "ALL",
  year: null,
};

/** 결제 이력 한 줄의 보조줄. `7장` / `6장 중 3장` / `전량 실패`.
 *
 * 날짜는 붙이지 않는다. 표기 규칙이 `format.ts` 에 있고 화면마다 폭이 달라서다.
 * 정본 목업 `A-43` 이 그리는 세 줄은 `09-06 · 7장` · `09-06 · 6장 중 3장` ·
 * `09-04 · 전량 실패` 이고, 날짜는 화면이 앞에 붙인다.
 *
 * **`N장 중 M장` 의 두 숫자 (확정, PRD 7.6.21)**
 * 앞은 **올린 장수**, 뒤는 **만든 장수 곧 청구 장수**다. **돌아간 장수를 쓰지 않는다.**
 * 같은 행의 금액이 `장당 청구가 × 청구 장수`라서, 뒤 숫자가 다른 것을 가리키면 셀러가 계산을
 * 맞출 수 없다. 이 화면의 존재 이유가 "왜 이만큼 나왔는지"다.
 *
 * **상태 낱말을 장수에 붙이지 않는다 (확정, PRD 7.6.21).** 전량 성공도 `7장` 까지만 쓰고
 * `완료`는 옆의 상태 칩이 말한다(`job-labels.ts`). 돌아간 사실도 마찬가지로 칩이 말한다
 * (`일부 돌아감` · `전부 돌아감`). 한 행이 같은 사실을 두 번 적지 않는다.
 * `W-31` 작업 바가 `7장 완료 · 현재 v1` 이라 쓰는 것은 그 자리에 칩이 없기 때문이고,
 * 이 함수와 모순이 아니다.
 *
 * `환불`은 셀러가 보는 자리에 쓰지 않는다(PRD 7.5.9).
 *
 * **`전량 실패`는 실패했을 때만 쓴다 (2026-09-08 이중 코드리뷰).** 예전 판은
 * `status === "REFUNDED"` 를 전량 실패로 단정했다. `RefundReason` 에는
 * `CANCELED_BEFORE_START` 와 `MANUAL` 이 있고 둘 다 전액 환불이라 상태가 똑같이
 * `REFUNDED` 가 된다. 그래서 **셀러가 시작 전에 취소해 전액이 돌아간 줄이 `전량 실패` 로
 * 적혔다.** 한 장도 만들려 시도한 적이 없는데 실패라고 말한 것이다. 7장을 다 만든 뒤 운영이
 * 수동으로 전액 환불한 경우도 같았다. 표본 `pay3` 이 사유와 결과가 우연히 일치해 가리고
 * 있었다. 대칭 표본이 결함을 가린 세 번째 사례다.
 *
 * **만든 장이 0인 줄의 낱말이 둘이다 (확정 2026-09-08, `design-manager`).**
 * `TOTAL_FAILURE` 는 `전량 실패`, `CANCELED_BEFORE_START` 는 `시작 전 취소`. 형식이 같다
 * (범위·시점 + 무슨 일). `시작 전` 은 `A-28` 프레임 제목 `결제됨 · 시작 전` 의 말이고
 * `취소` 는 용어 표가 "셀러가 시작 전에 스스로 무르는 일"에 배정한 낱말이다.
 * **`취소` 한 낱말로 두지 않은 이유**는 같은 계열에 `A-27` 결제 실패가 있어 `취소` 만
 * 적으면 "카드 결제가 취소됐다"로 읽히기 때문이다. */
export function paymentHistorySubline(item: PaymentHistoryItemView): string {
  const uploaded = item.uploadedQuantity;
  const made = item.billedQuantity;

  if (made === 0) {
    // 만든 장이 0. 사유가 낱말을 정한다. **장수로 사유를 대신하지 않는다** (PRD 7.6.21).
    // 그 규칙이 막는 것은 숫자 `0` 자체가 아니라 장수로 사유를 대신하는 것이다. 그래서
    // `8장 중 0장` 도 규칙을 피해 가지 못한다(폐기 근거는 DATA_DICTIONARY 7장).
    //
    // ⓐ 만들려 시도했고 다 실패했다 → `전량 실패`. 상태 칩이 아직 `REFUNDED` 로 넘어가지
    //    않은 구간에도 같은 줄이 나와야 하므로 상태가 아니라 장수와 사유로 판정한다.
    // ⓑ 시작 전 취소 → `시작 전 취소`. 배경 지우기를 **한 번도 부르지 않은** 상태다.
    //    `전량 실패` 가 "시도했다"고 거짓말하는 것과 같은 오류이고 방향만 다르다.
    if (item.refundReason === "CANCELED_BEFORE_START") return "시작 전 취소";
    if (item.refundReason !== "MANUAL") return "전량 실패";

    // ⓒ 운영의 수동 전액 환불인데 만든 장이 0. **사유를 여기 적지 않는 것만 확정이고
    //    (`D-47` ①) 낱말은 아직 없다.** 우리가 실패를 확인하지 못한 환불이라 ⓐ 로 넘길 수
    //    없고, 시도 여부도 알 수 없어 ⓑ 로도 넘길 수 없다. 그래서 이 한 갈래만 장수를
    //    그대로 적어 둔다. 낱말이 오면 이 줄이 바뀐다(DATA_DICTIONARY 6.5).
    return `${uploaded}장 중 0장`;
  }

  // 올린 장보다 만든 장이 적을 때만 `n장 중 m장` 을 쓴다.
  if (uploaded > made) return `${uploaded}장 중 ${made}장`;

  // **다시 만들기는 새 청구 항목이라**(`BillingItemKind.CUTOUT_RETRY`) 8장을 올려 8장을
  // 만들고 한 장을 다시 만들면 청구 장수가 9가 된다. 이때 `n장 중 m장` 을 쓸 수 없다.
  // **그 형식은 `m <= n` 을 전제하고**, 뒤가 크면 셀러는 줄이 고장 났다고 읽는다
  // (PRD 7.6.21, 확정 2026-09-08).
  //
  // 그렇다고 `9장` 하나로 뭉갤 수도 없다. 금액이 `단가 × 9` 라 보조줄은 9를 말해야 하는데,
  // `9장` 은 **9장을 올렸다**로 읽혀 올린 장수(8)와 어긋난다. 두 사실을 다 적는다.
  //
  // **낱말이 `다시 시도` 에서 `다시 만들기` 로 바뀌었다 (2026-09-13).** 용어 표(PRD 7.5.9)가
  // **`다시 시도` 를 업로드 쪽(실패한 장을 다시 거는 일)에 배정**하고, **이미 만들어진 장을
  // 다시 불러 새로 만드는 일(돈이 든다)에는 `다시 만들기`** 를 배정했다. 이 줄이 세는 것은
  // **올린 장수를 넘긴 청구 장수**이고, 그 초과분이 생기는 경로는 다시 만들기 하나뿐이다
  // (실패한 장을 되살려 성공해도 그 장은 올린 장 안에 있어 `made > uploaded` 가 되지
  // 않는다). 그러므로 이 자리의 옛 낱말은 **다른 동작의 이름을 빌려 쓰고 있었다.**
  // 낱말을 즉석에서 고른 것이 아니라 표가 이미 배정한 쪽으로 옮긴 것이다.
  if (made > uploaded) return `${uploaded}장 · 다시 만들기 ${made - uploaded}장`;
  return `${made}장`;
}

/** 줄의 상태 칩. **`paymentStatusLabel` 이 혼자 답할 수 없는 갈래가 하나 있다**(PRD 7.6.21
 * 칩 표 둘째 줄).
 *
 * `청구 없음` 의 판정 기준은 **상태가 아니라 금액**이다. 아직 결제되지 않았는데(`PENDING`)
 * 청구될 금액이 0인 줄(전량 실패 · 시작 전 취소)이다. 그 줄에 `결제 전` 을 달면 **곧
 * 무엇인가 결제될 것처럼 읽힌다.**
 *
 * ⛔ **`paymentStatusLabel` 에 금액 인자를 얹지 않는다.** 축이 둘인 판정을 그 함수에 넣으면
 * 호출부마다 다르게 부르게 된다. **그 금지는 상태 하나만 받는 함수에 대한 것이고, 줄을
 * 통째로 받는 이 함수는 해당하지 않는다** (2026-09-14 이중 코드리뷰). 앱과 웹이 같은 구현을
 * 한 벌씩 들고 있었고, 두 파일이 나란히 "앱과 같아야 한다"고 적어 두는 것은 `packages/shared`
 * 를 둔 이유와 정반대였다. 표 다섯 줄의 정본은 `7.6.21` 이고 화면이 다시 정하지 않는다. */
export function paymentHistoryChip(item: PaymentHistoryItemView): StatusLabel {
  if (item.status === "PENDING" && item.netAmount === 0) {
    return { tone: "idle", label: "청구 없음" };
  }
  return paymentStatusLabel(item.status);
}

/** `2026-09` → `2026년 9월`. **서버가 묶고 화면은 문구만 만든다**(`PaymentMonthGroupView`).
 *
 * 못 읽는 값이 오면 받은 그대로 적는다. 머리글 자리를 비우면 그 줄들이 어느 달 것인지 말할
 * 것이 없어진다. **한 셀러의 같은 9월을 앱과 웹이 다르게 부르지 않는다.** */
export function paymentMonthLabel(yearMonth: string): string {
  const [year, month] = yearMonth.split("-");
  const n = Number(month);
  if (!year || !month || !Number.isFinite(n)) return yearMonth;
  return `${year}년 ${n}월`;
}

/** 줄의 보조줄 전체. `09-13 14:21 · 1장`.
 *
 * 시각은 `paymentHistoryOccurredAt` 이 고른 값이고 뒤 문장은 `paymentHistorySubline` 이
 * 만든다. **조립까지 여기서 한다.** 앱과 웹이 각자 이어 붙이던 동안 두 파일이 구분자와
 * 순서를 한 벌씩 들고 있었고, 그 둘이 갈리면 같은 줄이 두 문장으로 보인다.
 *
 * 시각 조각은 빠질 수 있다(`formatDateTime` 은 못 읽은 시각에 빈 문자열을 준다). 그때
 * 템플릿에 그대로 끼우면 `· 1장` 처럼 앞에 구분자만 남으므로 빈 조각을 걸러 낸다.
 *
 * @param year 연도를 켜는가. **열 단위로 판정해서 넘긴다**(PRD 7.5.13 규칙 4). 줄마다 스스로
 *             판단하면 한 열에 `09-13 14:21` 과 `2025-12-30 14:21` 이 섞인다. */
export function paymentHistoryDetailLine(
  item: PaymentHistoryItemView,
  opts: { year: boolean } = { year: false },
): string {
  return [
    formatDateTime(paymentHistoryOccurredAt(item), { year: opts.year }),
    paymentHistorySubline(item),
  ]
    .filter(Boolean)
    .join(" · ");
}

// ── 작업 한 건의 청구 ────────────────────────────────────────────────────────

/** 작업 화면이 묻는 **"이 작업에 얼마가 들었나"**(`A-12` 의 `이번 작업에 든 금액`).
 *
 * **이 타입이 있는 이유가 하나다: 화면이 곱하지 않게 하는 것.** `A-12` 의 합계표는
 * 2026-09-14 까지 `cutoutPrice(만든 장) + cutoutPrice(더 부른 횟수)` 를 화면에서 곱하고
 * 있었고, 그래서 **같은 작업이 두 화면에서 다른 금액으로 보였다**. 8장을 올려 7장이 성공하고
 * 1장을 되살려 성공하면 청구 내역은 ₩4,000 인데 그 표는 ₩4,500 이었다. 이 파일 머리말의
 * ⛔ 두 줄(`JobEvent.costAmount` 금지 · 호출 횟수 × 단가 금지)을 그 표가 정확히 어기고 있었다.
 *
 * **그래서 여기에도 `단가`·`호출 횟수` 칸을 두지 않는다.** 칸이 있으면 누군가 곱한다.
 *
 * **합계 칸도 두지 않는다.** 표가 그리는 줄이 둘이고 그 둘을 더한 값을 쓰는 자리가 없다.
 * 필요해지는 날 서버가 내려주고, 그때까지 화면이 더하지 않는다. */
export interface JobBillingView {
  jobId: string;
  /** 이 작업의 **살아 있는 결제** id. 청구 행이 아직 하나도 없으면 `null` 이다.
   *
   * **정산 명세로 가는 열쇠가 이 칸이다.** `A-31`·`A-32`·`W-30` 은 `jobId` 만 들고 있는데
   * 명세는 `paymentId` 로만 열린다(한 작업에 결제가 여럿 남을 수 있어서다).
   *
   * ⚠️ **그 짝을 청구 내역 목록에서 찾으면 안 된다.** 그 응답은
   * `PAYMENT_HISTORY_MAX_ROWS` 에서 잘리므로 **줄이 500 을 넘은 셀러는 오래된 작업의 명세를
   * 영영 열지 못하고, 화면은 "기록을 찾지 못했습니다"라고 거짓말한다**(2026-09-14 이중
   * 코드리뷰). 상한을 쪽 나누기 대신으로 정한 결정과 그 조회가 서로를 모르고 있었다.
   * 서버가 `jobId` 로 직접 찾아 이 칸에 담는다. */
  paymentId: string | null;
  /** 배경 지우기로 **청구된 장수**. `kind = 'CUTOUT'` 이고 `billable = 1` 인 행의 수다.
   *
   * `JobImage` 행 수도 `DONE` 장 수도 아니다. 이미 있던 결과를 그대로 준 장은 공급자를
   * 부르지 않아 행이 없고(청구도 없다), 실패한 호출은 행이 있어도 `billable = 0` 이다. */
  cutoutQuantity: number;
  /** 그 장들에 셀러가 부담하는 금액. `Σ amount − Σ 환불` 이고 **화면이 다시 곱하지 않는다.** */
  cutoutAmount: number;
  /** 다시 만들기로 청구된 **회수**. 단위가 `장` 이 아니라 `회` 인 것은 같은 장을 두 번 다시
   *  만들면 1장에 2회이기 때문이다(`A-25` 의 횟수 줄과 같은 축).
   *
   * ⚠️ **`JobImage.providerCallCount` 로 세던 값과 다르다.** 그쪽은 실패한 호출도 함께
   * 세는데 청구는 성공한 호출에만 붙는다. 그 수에 단가를 곱하면 실제 청구보다 커진다. */
  remakeQuantity: number;
  remakeAmount: number;
}

// ── 정산 명세 ────────────────────────────────────────────────────────────────

/** 못 만든 장 한 줄.
 *
 * ⭐ **장은 번호로 부른다** (2026-09-21 목업 `A-43b` 개정 · `A-43c`. PRD `7.6.21c` 표의
 * `2번 사진` 줄). 이 줄은 2026-09-27 까지 `originalFilename` 을 들고 `못 만든 1장 IMG_4024`
 * 로 그렸는데 **목업이 그 판을 철회했다.**
 *
 * | 왜 파일 이름을 걷었나 |
 * |---|
 * | `IMG_4024` 는 **셀러의 폰이 지은 이름**이지 셀러가 아는 이름이 아니다. 명세는 **돈이 왜 돌아왔는지**를 말하는 자리라 **셀러가 화면에서 본 차례**로 불러야 한다. 파일 이름을 그대로 쓰면 셀러가 어느 사진인지 맞춰 봐야 한다 |
 * | **서버는 파일명을 저장하지 않기로 했다**(`A-24c` 가 세운 규약). 없는 값을 명세에 적어 두면 **가장 정확해야 할 화면이 가장 먼저 거짓이 된다** |
 * | 저장돼 있어도 지목이 안 된다. `NULL` 일 수 있고 같은 이름이 둘일 수도 있다(`docs/DATA_DICTIONARY.md` 34.5) |
 *
 * **`4번 사진` 은 셀러가 올린 차례다.** 진행 화면 · 결과 화면 · 되살리기 시트 · 내보내기
 * 시트가 이미 `${sortOrder + 1}번 사진` 으로 부르고 있어서, 이 줄이 같은 축으로 오면
 * **앱에서는** 같은 장을 같은 이름으로 말한다.
 *
 * ⚠️ **웹과 이력에는 아직 안 닿는다**(2026-09-27 코드리뷰가 실측했다). 세 자리가 여전히
 * 파일 이름을 **먼저** 쓴다 — `apps/web/src/screens/Review.tsx`(게다가 대체값이 배열
 * 인덱스다) · `apps/web/src/screens/stages/Result.tsx` 의 스크린리더 문장 ·
 * `RETRY_REQUESTED` 이벤트의 `detail`(앱 활동 이력이 그대로 그린다. 바로 아래
 * `REMAKE_REQUESTED` 는 `4번 사진` 이라 **한 타임라인이 같은 장을 두 이름으로** 부른다).
 * 업로드가 파일 이름을 거의 언제나 채우므로 **실제로 `IMG_4024` 가 뜬다.**
 * ⛔ **이 줄이 「닫혔다」고 적지 않는다** — 다음 사람이 그 단정을 근거로 대조를 건너뛴다.
 *
 * ⚠️ **실측 하나를 함께 적어 둔다** (2026-09-27). 목업과 PRD 는 "서버는 파일명을 저장하지
 * 않는다"고 적었지만 **`JobImage.originalFilename` 컬럼은 실재하고 업로드가 값을 채운다**
 * (`0002` 231행 · `routes/jobs.ts` 업로드 절). 곧 규약은 **"저장하지 않는다"가 아니라
 * "셀러에게 그 이름으로 말하지 않는다"** 가 참이다. 그 갈림은 `data-manager` 가 문서에
 * 적을 일이고, **이 타입에서는 그 값을 받지 않는 것으로 닫는다.**
 *
 * ## 사유 칸은 여기 없다. 자리만 열어 둔다 (`13.1` **O-65**. 사용자 결정 대기)
 *
 * `A-43c` 는 줄마다 **사유**(`경계를 못 찾음` · `우리 쪽에서 끊김`)를 적는다. **그 칸을 오늘
 * 선언하지 않는다.** 이유 셋이다.
 *
 * | # | 왜 |
 * |---|---|
 * | ① | **O-65 가 열려 있다.** 명세에 사유를 적을지 자체가 사용자 결정이다 |
 * | ② | **낱말이 없다.** 목업의 `경계를 못 찾음` 은 `failure-messages.ts` 의 `short`(`경계를 못 찾았어요`)와 다른 문장이고, 어느 쪽을 쓸지는 `design-manager` 소관이다 |
 * | ③ | **서버가 안 싣는 칸을 선언하면 화면에 빈 열이 선다.** 값이 오지 않는 칸은 타입이 아니라 함정이다 |
 *
 * **자리의 모양만 미리 적어 둔다**: 그 칸이 서는 날 이름은 `errorCode` 이고 값은
 * `JobFailureCode | null`(`JobImage.lastErrorCode` 가 정본)이다. **마이그레이션은 필요 없다.**
 * ⛔ **금액을 사유로 가르지 않는다** (PRD `7.6.21c` · 법무 `L-29` 가 지지했다). 사유가 셋이면
 * 셀러는 값이 다를 것이라 읽지만 **결과를 못 준 장은 어느 쪽 사정이든 청구하지 않는다.** */
export interface RefundedImageLineView {
  imageId: string;
  /** 셀러가 올린 차례. **`JobImage.sortOrder` 그대로 0 부터 센다**(`0001` 19행 ·
   *  UQ(jobId, sortOrder) 가 총 순서를 보장한다. **마이그레이션 없음**). 화면에 뜨는 번호는
   *  `sortOrder + 1` 이고 **그 덧셈은 `refundedImageLabel()` 한 곳에서만 한다.**
   *
   * ⛔ **응답 배열의 인덱스로 대신하지 마라.** 이 목록은 **못 만든 장만** 담아서 2·5·6번
   * 사진이 돌아간 건의 인덱스는 0·1·2 다. 인덱스로 부르면 **셀러가 본 번호와 다른 번호**가
   * 명세에 적힌다.
   *
   * ⚠️ **이 번호는 셀러가 사진 순서를 바꾸면 움직인다**(`ReorderJobImagesRequest`. 재정렬을
   * 막는 것은 `PROCESSING` 하나다. `routes/jobs.ts`). 명세는 **결판이 난 뒤에 읽는 문서**라
   * 그 뒤에 순서가 바뀌면 **이미 돌아간 돈이 다른 장의 이름으로 적힌다.** 번호를 얼려 두려면
   * 결판 시점의 순번을 환불 쪽에 저장해야 하고 **그것은 마이그레이션이라 사용자 승인
   * 대상이다**(`docs/DATA_DICTIONARY.md` 28.6 이 적어 둔 "결제사가 붙는 날 한 건"에 붙일 축).
   * 오늘은 읽는 시점의 `sortOrder` 로 그린다. */
  sortOrder: number;
  /** 그 장에 **셀러가 낸 금액**(청구가 기준. 예시의 `₩27` 은 청구가가 정해지기 전 목업
   *  숫자다). **오늘 단가를 다시 곱해 만들지 않는다**(`docs/DATA_DICTIONARY.md` 14.3 ③).
   *
   * ⚠️ **"값은 그 장의 `RefundItem.amount` 에서 온다"가 2026-09-21 에 정정됐다**
   * (`docs/DATA_DICTIONARY.md` 28.1·28.3). **`RefundItem` 에는 행이 하나도 없고, 오늘
   * 넣으면 장부가 틀린다** — 받은 돈이 0 이라 `netAmount` 가 `0 − 500` 이 된다. 그래서 이
   * 칸의 값은 **저장된 단가 스냅샷의 사슬**에서 온다:
   * ① 그 장의 `BillingItem.unitPriceAmount` → ② 없으면 `Payment.unitPriceAmount`.
   * **결제사가 붙으면 정본이 `RefundItem.amount` 로 돌아온다.** 그날 이 주석을 되돌린다.
   *
   * ⛔ **`BillingItem.providerCostAmount`(우리 원가)를 이 칸에 싣지 마라.** 셀러 응답에
   * 원가가 들어가면 화면에 안 그려도 샌 것이다(같은 문서 14.6 끝).
   *
   * ⚠️ **응답에는 실려 오지만 화면은 쓰지 않는다**(2026-09-27). `refundedImageBlock` 이
   *  돌려주는 블록에 금액 칸이 없고, 앱·웹 모두 그 블록만 그린다. 값이 남아 있는 이유는
   *  **감사**(어느 장에 얼마가 돌아갔는지 행 단위로 되짚을 수 있어야 한다)와 사유 칸이
   *  서는 날의 대비다.
   *
   * ⛔ **이 값을 화면에 적지 마라.** 줄마다 금액을 적으면 셀러는 **사유에 따라 값이
   * 다를 것**이라 읽는다(법무 `L-29`). 그리고 ⛔ **더하지 마라** — 합계는
   * `RefundStatementView.refundedAmount` 하나다. 지금 이 둘을 막는 것이 산문뿐이라
   * `packages/shared/test/refunded-image-block.test.mjs` 가 **블록 쪽에서** 못 박아 둔다. */
  amount: number;
}

/** 못 만든 장 하나를 셀러가 부르는 이름. `4번 사진`.
 *
 * ⭐ **이름 하나만 돌려준다.** 2026-09-27 까지 이 함수는 `못 만든 1장 IMG_4024` 를 만들었고
 * **문제가 둘이었다.**
 *
 * | 폐기된 것 (지우지 않고 남긴다) | 왜 뒤집혔나 |
 * |---|---|
 * | 라벨에 `못 만든 1장` 을 박아 두는 것 | 줄이 셋인 `A-43c` 에서 **같은 줄이 세 번 선다.** 셀러는 어느 장인지 못 가른다. **수는 줄이 아니라 블록 머리가 말한다**(`refundedImageBlock`) |
 * | 파일 이름으로 부르는 것 | `RefundedImageLineView` 머리말의 표 |
 *
 * ⛔ **여기서 `못 만든` 을 다시 붙이지 마라.** 이 문자열은 **한 줄에 하나씩** 서는 이름이고,
 * 수를 말하는 자리는 `refundedImageBlock().head` 하나다. 두 자리가 같은 사실을 적으면
 * `못 만든 3장` 아래에 `못 만든 1장` 이 셋 선다. */
export function refundedImageLabel(line: RefundedImageLineView): string {
  return `${line.sortOrder + 1}번 사진`;
}

/** `못 만든 장` 그룹의 한 줄(`A-43c`).
 *
 * ⛔ **금액 칸이 없다.** `A-43c` 는 **사유는 적고 금액은 가르지 않는다.** 줄마다 값을 적으면
 * 셀러는 **사유에 따라 값이 다를 것**이라 읽고, 그렇게 읽히면 **화면이 귀책을 판정하는
 * 자리**가 된다(법무 `L-29` 가 그 판단을 지지했다). 셀러 지갑으로 들어오는 값은
 * `RefundStatementView.refundedAmount` 한 줄뿐이다. */
export interface RefundedImageRowView {
  imageId: string;
  /** `refundedImageLabel()` 이 만든 이름. */
  name: string;
}

/** 못 만든 장 블록(`A-43b`·`A-43c` 가 같은 화면의 두 모양으로 쓰는 것).
 *
 * **머리가 수를 말하고 줄이 장을 말한다.** 이 구조는 `A-52c` 의 못 만든 장 블록
 * (같은 패키지 `export-decline.ts` 의 `ExportDeclineBlock`)과 **같은 규격이다.**
 * 같은 사실을 두 화면이 다른 모양으로 말하지 않게 맞춰 둔다. */
export interface RefundedImageBlockView {
  /** 명세 그룹의 `못 만든 3장` 줄 라벨.
   *
   * ⛔ **값 칸을 이 타입이 만들지 않는다.** 그 줄의 금액은
   * `RefundStatementView.refundedAmount` 이고 **화면이 `amount` 를 더해 만들지 않는다**
   * (이 파일 머리말의 곱셈·합산 금지 두 줄). */
  head: string;
  /** 머리 **라벨 안**의 모노. **줄이 하나일 때만** 선다(`못 만든 1장` + `4번 사진`.
   *  `A-43b` 가 그 모양이다). 둘 이상이면 그 자리가 어느 장인지 못 가리키므로 `null` 이고
   *  줄로 내려온다.
   *
   * ⚠️ **값 칸이 아니다.** 목업은 `<span>못 만든 1장 <span class="mono">4번 사진</span></span>`
   * 으로 **라벨 안**에 두었고 값 칸에는 금액(`₩500`)이 있다. 이 이름을 값 칸으로 보내면
   * **금액이 밀려난다** — 웹의 `SettingRow` 는 값 칸을 언제나 `mono` 로 감싸서 그 실수가
   * 그럴듯하게 보인다.
   *
   * ⛔ **`2·5·6번 사진` 으로 잇지 않는다.** 모노 한 칸은 **식별자 하나의 자리**다. */
  trailing: { mono: string } | null;
  /** `못 만든 장` 그룹의 줄들.
   *
   * **줄이 하나면 빈 배열이다.** 그때는 머리가 이미 그 장을 가리켜서 `A-43b` 에 그 그룹이
   * 없다. 같은 장을 두 자리에 적으면 한 화면이 같은 사실을 두 번 말한다.
   *
   * ⛔ **접지 않는다.** `외 n장` 으로 접힌 장은 **셀러가 보지 못한 채 결판난 장**이 된다. */
  rows: RefundedImageRowView[];
}

/** 못 만든 장 목록에서 블록 하나를 만든다. **`null` 이면 그 블록이 안 선다**(전량 성공한 건).
 *
 * ⛔ **화면이 이 가름을 다시 하지 않는다.** 화면 안의 조건 한 줄은 어떤 시험도 보지 못하고,
 * 이 자리에서 틀릴 수 있는 것이 셋이다: **수를 두 자리에 적는 것** · **줄이 하나인데 그룹을
 * 세우는 것** · **줄이 여럿인데 머리가 한 장만 가리키는 것.**
 *
 * ⛔ **`못 만든 0장` 을 만들지 않는다.** 그래서 빈 목록은 `null` 이다. 전량 성공한 건의 명세
 * 에는 이 블록 자체가 없다. */
export function refundedImageBlock(
  lines: readonly RefundedImageLineView[],
): RefundedImageBlockView | null {
  // ⛔⛔ **장으로 접는다. 줄 수를 장수라고 적지 않는다** (2026-09-27 코드리뷰가 잡았다).
  //
  // 한 장은 청구 행을 **여럿** 가진다(`BillingItem_imageId_attemptNo_uq` 는 `(imageId,
  // attemptNo)` 다). 환불 행의 유니크는 `billingItemId` 하나라, **같은 장의 두 시도가
  // 각각 환불 행을 갖는다.** 서버 질의에 `DISTINCT` 가 없고 `ORDER BY i."sortOrder",
  // b."attemptNo"` 가 그 여럿을 **스스로 전제**한다.
  //
  // 접지 않으면 사진 한 장이 **`못 만든 2장`** 이 되고, 그룹에 `4번 사진` 이 **두 번** 서고,
  // 화면의 `key` 가 겹치고, 이 파일 아래가 적어 둔 불변식
  // (`madeQuantity + failedLines.length` = 올린 장수)이 깨져 **`만든 3장` + `못 만든 3장`
  // 이 올린 5장을 반박한다.** ⚠️ 오늘은 못 닿는다 — `RefundItem` 에 행을 넣는 코드가 아직
  // 없다(`verification/cases/legal-premises.json` 의 `refund-item-rows-are-never-inserted`).
  // **그 코드가 서는 날 이 접기가 없으면 셀러가 읽는 장수가 틀린다.**
  //
  // ⛔ **금액을 여기서 합치지 않는다.** 접는 것은 **이름**뿐이다. 두 시도의 금액을 더하면
  // 이 파일이 금지한 합산이 되고, 어차피 이 블록은 금액을 싣지 않는다.
  const byImage = new Map<string, RefundedImageLineView>();
  for (const line of lines) {
    // **첫 줄을 남긴다.** 질의가 `attemptNo` 오름차순이라 첫 줄이 그 장의 첫 시도이고,
    // 이름은 `sortOrder` 에서만 오므로 어느 시도를 남겨도 이름은 같다. 순서를 지키려고
    // 첫 것을 잡는다 — `set` 을 덮어쓰면 Map 의 자리는 그대로지만 값이 바뀌어,
    // 나중에 줄에 무엇이 더 실릴 때 **마지막 시도로 조용히 갈린다.**
    if (!byImage.has(line.imageId)) byImage.set(line.imageId, line);
  }
  const images = [...byImage.values()];
  if (images.length === 0) return null;
  // **줄이 하나일 때만 머리가 그 장을 가리킨다.** 길이를 두 번 보지 않고 그 줄 자체를 잡아
  // 둔다. 인덱스로 다시 집으면 "길이를 봤으니 있다"를 타입이 못 따라온다.
  const [only] = images.length === 1 ? images : [];
  return {
    head: `못 만든 ${images.length}장`,
    trailing: only ? { mono: refundedImageLabel(only) } : null,
    rows: only
      ? []
      : images.map((line) => ({ imageId: line.imageId, name: refundedImageLabel(line) })),
  };
}

/** 정산 명세(`A-43b`·`W-70` 우측). **화면 제목은 `정산 명세`다** — 타입 이름만
 * `RefundStatementView` 로 남긴다(PRD 7.5.9: `환불 명세`는 쓰지 않는 말).
 *
 * **이 화면의 핵심은 두 시각을 갈라 적는 것이다.** 우리가 보낸 시각과 셀러 카드에 찍히는
 * 시각은 다르고, "환불했다는데 돈이 안 들어왔다"가 가장 흔한 문의가 될 자리다. 그래서
 * `sentAt` 하나로 뭉개지 않고 `requestedAt` 과 갈라 둔다. 카드에 찍히는 시각은 우리가
 * 알 수 없으므로 필드가 없다. 화면이 "며칠 걸립니다"로만 말한다. */
export interface RefundStatementView {
  paymentId: string;
  jobId: string;
  productName: string | null;

  /** 결제한 금액. 환불 전 총액이다. */
  paidAmount: number;
  /** 만든 장수와 그 금액. **`PaymentHistoryItemView.billedQuantity` 와 같은 값이다.**
   *  만든 장수가 곧 청구 장수다(PRD 7.6.21). 두 화면이 다른 숫자를 말하면 명세가
   *  이력을 반박하게 된다. */
  madeQuantity: number;
  madeAmount: number;
  /** 못 만든 장. 줄마다 어느 사진인지 적는다.
   *
   * ⭐ **2026-09-21 에 데이터 쪽이 닫혔다** (`7.13` **D-77** · `docs/DATA_DICTIONARY.md` 28.4).
   * ~~환불 경계가 "provider 를 호출했는가"라 `providerCallCount = 0` 으로 끝난 장은 환불 줄이
   * 없다.~~ **경계는 이제 "결과물을 줄 수 있었는가" 하나이고 원인으로 가르지 않는다.**
   * 부르기 전에 끊긴 장(`NOT_CONFIGURED`·`SOURCE_MISSING`·`SOURCE_TOO_LARGE`)도 **이 목록에
   * 선다.** 금액은 위 `amount` 의 사슬 ② 가 만든다. **`₩0` 으로 적는 안은 폐기다.**
   *
   * ⚠️ **그래서 `madeQuantity + failedLines.length` 는 이제 올린 장수와 같아야 한다.**
   * 어긋나면 서버 질의가 한 갈래를 빠뜨린 것이다. ⛔ **다만 그 줄의 낱말은 아직 없다**
   * (`design-manager` `D-47` ②. `docs/DATA_DICTIONARY.md` 6.5). 여기서 문장을 짓지 않는다.
   * 이력 보조줄은 `uploadedQuantity` 로 그 자리를 메웠다. */
  failedLines: RefundedImageLineView[];
  /** 돌아간 금액. `failedLines` 합계와 같아야 한다. 어긋나면 환불 항목 쪽이 정본이다. */
  refundedAmount: number;

  reason: RefundReason | null;
  /** 우리가 환불을 만든 시각(`요청`). */
  requestedAt: string | null;
  /** 결제사에 실제로 나간 시각(`보낸 시각`). 아직이면 `null`. */
  sentAt: string | null;
  /** `신한 1234`. 표시용 요약만 저장한다. 카드번호·유효기간·CVC 는 두지 않는다. */
  destinationLabel: string | null;

  /** `null` 이면 화면이 `실행 방식 미정` 배지를 단다. **문구가 통째로 갈린다**.
   *  전액 받고 못 만든 장 금액을 취소로 처리하면 `돌아갔습니다`가 맞고, 가승인 뒤 성공분만
   *  매입하면 `청구되지 않았습니다`가 맞는 말이 된다(PRD 7.5.9 의 O-13 표). 결제사 선정
   *  전에는 정할 수 없다. */
  settlementMode: SettlementMode | null;
}
